// ./src/utils/geo.ts

// ============================================
// Geo Utilities
// Distance calculations and coordinate helpers
// ============================================

import { GeoCoords } from "@/hooks/useGeolocation";

// ============================================
// Constants
// ============================================

/** Earth's radius in meters */
const EARTH_RADIUS_M = 6371000;

/** Default distance threshold for "same location" checks (meters) */
export const DEFAULT_DISTANCE_THRESHOLD_M = 30;

/** Default max age for location freshness (5 minutes in ms) */
export const DEFAULT_MAX_AGE_MS = 5 * 60 * 1000;

// ============================================
// Distance Calculations
// ============================================

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate the Haversine distance between two coordinates
 * Returns distance in meters
 *
 * @param coord1 - First coordinate
 * @param coord2 - Second coordinate
 * @returns Distance in meters
 *
 * @example
 * ```ts
 * const distance = haversineDistanceM(
 *   { latitude: 51.5074, longitude: -0.1278 }, // London
 *   { latitude: 48.8566, longitude: 2.3522 }   // Paris
 * );
 * // Returns ~343,000 meters (343 km)
 * ```
 */
export function haversineDistanceM(
  coord1: GeoCoords,
  coord2: GeoCoords
): number {
  const lat1Rad = toRadians(coord1.latitude);
  const lat2Rad = toRadians(coord2.latitude);
  const deltaLat = toRadians(coord2.latitude - coord1.latitude);
  const deltaLng = toRadians(coord2.longitude - coord1.longitude);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1Rad) *
    Math.cos(lat2Rad) *
    Math.sin(deltaLng / 2) *
    Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_M * c;
}

/**
 * Calculate the Haversine distance between two coordinates
 * Returns distance in kilometers
 *
 * @param coord1 - First coordinate
 * @param coord2 - Second coordinate
 * @returns Distance in kilometers
 */
export function haversineDistanceKm(
  coord1: GeoCoords,
  coord2: GeoCoords
): number {
  return haversineDistanceM(coord1, coord2) / 1000;
}

// ============================================
// Location Comparison Helpers
// ============================================

/**
 * Check if two coordinates are within a given distance threshold
 *
 * @param coord1 - First coordinate
 * @param coord2 - Second coordinate
 * @param thresholdM - Distance threshold in meters (default: 100m)
 * @returns True if coordinates are within threshold distance
 *
 * @example
 * ```ts
 * const isSameArea = areCoordinatesNear(userCoords, cachedCoords, 100);
 * ```
 */
export function areCoordinatesNear(
  coord1: GeoCoords,
  coord2: GeoCoords,
  thresholdM: number = DEFAULT_DISTANCE_THRESHOLD_M
): boolean {
  const distance = haversineDistanceM(coord1, coord2);
  return distance <= thresholdM;
}

/**
 * Check if a coordinate is valid (within valid lat/lng ranges)
 *
 * @param coords - Coordinates to validate
 * @returns True if coordinates are valid
 */
export function isValidCoordinate(coords: GeoCoords | null | undefined): boolean {
  if (!coords) return false;

  const { latitude, longitude } = coords;

  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return false;
  }

  if (isNaN(latitude) || isNaN(longitude)) {
    return false;
  }

  // Valid latitude range: -90 to 90
  if (latitude < -90 || latitude > 90) {
    return false;
  }

  // Valid longitude range: -180 to 180
  if (longitude < -180 || longitude > 180) {
    return false;
  }

  return true;
}

/**
 * Find the nearest location from a list of coordinates
 *
 * @param target - Target coordinate to compare against
 * @param locations - Array of locations with coordinates
 * @param getCoords - Function to extract coordinates from location object
 * @returns The nearest location and its distance, or null if no locations
 *
 * @example
 * ```ts
 * const nearest = findNearestLocation(
 *   userCoords,
 *   cachedLocations,
 *   (loc) => loc.coords
 * );
 * if (nearest && nearest.distance < 100) {
 *   // Use cached location
 * }
 * ```
 */
export function findNearestLocation<T>(
  target: GeoCoords,
  locations: T[],
  getCoords: (item: T) => GeoCoords
): { item: T; distance: number } | null {
  if (!locations.length || !isValidCoordinate(target)) {
    return null;
  }

  let nearest: { item: T; distance: number } | null = null;

  for (const item of locations) {
    const coords = getCoords(item);

    if (!isValidCoordinate(coords)) continue;

    const distance = haversineDistanceM(target, coords);

    if (!nearest || distance < nearest.distance) {
      nearest = { item, distance };
    }
  }

  return nearest;
}

// ============================================
// Coordinate Formatting
// ============================================

/**
 * Format coordinates to a specified decimal precision
 *
 * @param coords - Coordinates to format
 * @param precision - Number of decimal places (default: 6, ~0.1m precision)
 * @returns Formatted coordinates
 */
export function formatCoordinates(
  coords: GeoCoords,
  precision: number = 6
): GeoCoords {
  return {
    latitude: Number(coords.latitude.toFixed(precision)),
    longitude: Number(coords.longitude.toFixed(precision)),
  };
}

/**
 * Format coordinates as a display string
 *
 * @param coords - Coordinates to format
 * @param precision - Number of decimal places (default: 5)
 * @returns Formatted string like "51.50740, -0.12780"
 */
export function formatCoordsString(
  coords: GeoCoords,
  precision: number = 5
): string {
  return `${coords.latitude.toFixed(precision)}, ${coords.longitude.toFixed(precision)}`;
}

// ============================================
// Bounding Box Helpers
// ============================================

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Create a bounding box around a coordinate with a given radius
 *
 * @param center - Center coordinate
 * @param radiusM - Radius in meters
 * @returns Bounding box
 */
export function createBoundingBox(
  center: GeoCoords,
  radiusM: number
): BoundingBox {
  // Approximate degrees per meter at given latitude
  const latDegPerM = 1 / 111320;
  const lngDegPerM = 1 / (111320 * Math.cos(toRadians(center.latitude)));

  const latOffset = radiusM * latDegPerM;
  const lngOffset = radiusM * lngDegPerM;

  return {
    north: center.latitude + latOffset,
    south: center.latitude - latOffset,
    east: center.longitude + lngOffset,
    west: center.longitude - lngOffset,
  };
}

/**
 * Check if a coordinate is within a bounding box
 *
 * @param coords - Coordinate to check
 * @param box - Bounding box
 * @returns True if coordinate is within the box
 */
export function isWithinBoundingBox(
  coords: GeoCoords,
  box: BoundingBox
): boolean {
  return (
    coords.latitude >= box.south &&
    coords.latitude <= box.north &&
    coords.longitude >= box.west &&
    coords.longitude <= box.east
  );
}